/**
 * UserPortalModule - Controller for User Portal
 *
 * Manages user profile display and preference updates.
 * Connects UI component with UserProfileService.
 *
 * Responsibilities:
 * - Initialize and manage UserPortal component
 * - Load profile on auth state change
 * - Handle preference updates
 * - Clear state on sign out
 */
export class UserPortalModule {
    /**
     * Create UserPortalModule
     * @param {import('../services/user-profile-service.js').UserProfileService} profileService
     * @param {{ theme?: import('./theme.js').ThemeModule|null }} [options] Optional DI:
     *   - theme: apply theme changes optimistically for instant UI response.
     *     The authoritative apply happens via profileService.onStateChange()
     *     wired in main.js, so this module only needs to fire-and-forget.
     */
    constructor(profileService, { theme = null } = {}) {
        if (!profileService) {
            throw new Error('ProfileService is required');
        }
        this.profileService = profileService;
        this.theme = theme;
        this.container = null;
        this.portal = null;
        this.currentUser = null;
        this.unsubscribeAuth = null;
    }

    /**
     * Initialize the module
     * @param {string} containerId - ID of container element
     */
    init(containerId) {
        this.container = document.getElementById(containerId);
        if (!this.container) {
            console.warn(`UserPortalModule: Container #${containerId} not found`);
            return;
        }

        this.createPortalComponent();
        this.setupEventListeners();
    }

    /**
     * Create and mount the UserPortal component
     */
    createPortalComponent() {
        this.portal = /** @type {import('../components/UserPortal.js').UserPortal} */ (
            document.createElement('user-portal')
        );
        this.container?.appendChild(this.portal);
    }

    /**
     * Setup event listeners for preference changes
     */
    setupEventListeners() {
        if (!this.portal) return;

        this.portal.addEventListener('preference-change', async (event) => {
            const { key, value } = /** @type {CustomEvent} */ (event).detail;
            await this.handlePreferenceChange(key, value);
        });
    }

    /**
     * Handle user authentication state change
     * @param {Object|null} user - Firebase Auth user
     */
    async handleAuthStateChange(user) {
        this.currentUser = user;

        if (user) {
            await this.loadProfile(user);
        } else {
            this.clearProfile();
        }
    }

    /**
     * Load user profile
     * @param {Object} user - Firebase Auth user
     */
    async loadProfile(user) {
        if (!this.portal) return;

        this.portal.setLoading(true);

        const result = await this.profileService.getOrCreateProfile(user);

        if (result.success) {
            this.portal.setProfile(result.data);
        } else {
            this.portal.setError(result.error || 'Failed to load profile');
        }
    }

    /**
     * Handle preference change from UI
     * @param {string} key - Preference key
     * @param {*} value - New value
     */
    async handlePreferenceChange(key, value) {
        if (!this.currentUser) return;

        // Optimistic apply for instant UI response. On failure, the profile
        // reload below re-fires ThemeModule.onProfileLoaded via the
        // profileService.onStateChange subscription registered in main.js,
        // so the reverted value automatically re-applies.
        if (key === 'theme' && this.theme) {
            this.theme.applyPreference(value);
        }

        const preferences = { [key]: value };
        const result = await this.profileService.updatePreferences(
            this.currentUser.uid,
            preferences,
        );

        if (!result.success) {
            console.error('Failed to update preference:', result.error);
            // Reload profile to revert UI to actual state
            await this.loadProfile(this.currentUser);
        }
    }

    /**
     * Clear profile state (on sign out)
     */
    clearProfile() {
        if (this.currentUser) {
            this.profileService.clearProfile(this.currentUser.uid);
        }
        if (this.portal) {
            this.portal.clear();
        }
    }

    /**
     * Cleanup resources
     */
    destroy() {
        if (this.unsubscribeAuth) {
            this.unsubscribeAuth();
        }
        if (this.portal && this.container) {
            this.container.removeChild(this.portal);
        }
        this.portal = null;
        this.container = null;
        this.currentUser = null;
    }
}
